import React, { forwardRef, useMemo } from 'react';
import {
  Button,
  ButtonProps,
  IconButton,
  IconButtonProps,
  CircularProgress,
  Box,
  Tooltip,
  Badge,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { cva, type VariantProps } from 'class-variance-authority';
import { clsx } from 'clsx';
import {
  Search as SearchIcon,
  Refresh as ResetIcon,
  Edit as EditIcon,
  FileDownload as DownloadIcon,
  PlayArrow as PlayIcon,
  Delete as DeleteIcon,
  Add as AddIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
  Visibility as ViewIcon,
  MoreVert as MoreIcon,
  FilterList as FilterIcon,
  Settings as SettingsIcon,
  Info as InfoIcon,
  Warning as WarningIcon,
  CheckCircle as SuccessIcon,
  Error as ErrorIcon,
} from '@mui/icons-material';
import { SAP_COLORS, SAP_SPACING, SAP_TYPOGRAPHY, SAP_SHADOWS, SAP_TRANSITIONS } from '../../theme/sapDesignSystem';

// Icon type mapping
export type IconType = 
  | 'search' 
  | 'reset' 
  | 'edit' 
  | 'download' 
  | 'play' 
  | 'delete'
  | 'add'
  | 'save'
  | 'cancel'
  | 'view'
  | 'more'
  | 'filter'
  | 'settings'
  | 'info'
  | 'warning'
  | 'success'
  | 'error';

export type ButtonVariant = 'primary' | 'secondary' | 'success' | 'warning' | 'error' | 'ghost' | 'outlined';
export type ButtonSize = 'xs' | 'sm' | 'md' | 'lg' | 'xl';

// Component variants using CVA
const buttonVariants = cva(
  'transition-all duration-200 ease-in-out font-medium rounded-md focus:outline-none focus:ring-2 focus:ring-offset-2',
  {
    variants: {
      variant: {
        primary: 'bg-blue-600 text-white hover:bg-blue-700 focus:ring-blue-500',
        secondary: 'bg-gray-100 text-gray-700 hover:bg-gray-200 focus:ring-gray-500',
        success: 'bg-green-600 text-white hover:bg-green-700 focus:ring-green-500',
        warning: 'bg-orange-600 text-white hover:bg-orange-700 focus:ring-orange-500',
        error: 'bg-red-600 text-white hover:bg-red-700 focus:ring-red-500',
        ghost: 'bg-transparent text-gray-700 hover:bg-gray-100 focus:ring-gray-500',
        outlined: 'border border-gray-300 bg-transparent text-gray-700 hover:bg-gray-50 focus:ring-gray-500',
      },
      size: {
        xs: 'h-6 px-2 text-xs',
        sm: 'h-8 px-3 text-sm',
        md: 'h-10 px-4 text-base',
        lg: 'h-12 px-6 text-lg',
        xl: 'h-14 px-8 text-xl',
      },
      fullWidth: {
        true: 'w-full',
        false: 'w-auto',
      },
      disabled: {
        true: 'opacity-50 cursor-not-allowed',
        false: '',
      },
    },
    defaultVariants: {
      variant: 'primary',
      size: 'md',
      fullWidth: false,
      disabled: false,
    },
  }
);

// Enhanced TypeScript interface
export interface SAPButtonProps extends Omit<ButtonProps, 'variant' | 'size'> {
  // Custom props
  variant?: ButtonVariant;
  size?: ButtonSize;
  fullWidth?: boolean;
  
  // Enhanced accessibility
  ariaLabel?: string;
  ariaDescribedBy?: string;
  
  // Loading state
  loading?: boolean;
  loadingText?: string;
  
  // Icon support
  icon?: IconType | React.ReactNode;
  iconPosition?: 'start' | 'end';
  iconOnly?: boolean;
  
  // Badge support
  badge?: number | string;
  badgeColor?: 'primary' | 'secondary' | 'error' | 'warning' | 'info' | 'success';
  
  // Tooltip
  tooltip?: string;
  
  // Custom styling
  customStyles?: React.CSSProperties;
  
  // Callbacks
  onClick?: () => void;
}

// Icon mapping
const iconMap = {
  search: SearchIcon,
  reset: ResetIcon,
  edit: EditIcon,
  download: DownloadIcon,
  play: PlayIcon,
  delete: DeleteIcon,
  add: AddIcon,
  save: SaveIcon,
  cancel: CancelIcon,
  view: ViewIcon,
  more: MoreIcon,
  filter: FilterIcon,
  settings: SettingsIcon,
  info: InfoIcon,
  warning: WarningIcon,
  success: SuccessIcon,
  error: ErrorIcon,
};

// Default tooltips for common actions
const defaultTooltips: Record<IconType, string> = {
  search: 'Search',
  reset: 'Reset filters',
  edit: 'Edit',
  download: 'Download',
  play: 'Run',
  delete: 'Delete',
  add: 'Add new',
  save: 'Save',
  cancel: 'Cancel',
  view: 'View details',
  more: 'More options',
  filter: 'Filter',
  settings: 'Settings',
  info: 'Information',
  warning: 'Warning',
  success: 'Success',
  error: 'Error',
};

// Styled Button following SAP design principles
const StyledButton = styled(Button, {
  shouldForwardProp: (prop) => !['size', 'variant', 'loading', 'customStyles'].includes(prop as string),
})<{
  variant?: ButtonVariant;
  size?: ButtonSize;
  loading?: boolean;
  customStyles?: React.CSSProperties;
}>(({ theme, variant = 'primary', size = 'md', loading, customStyles }) => {
  // Size configurations
  const sizeConfig = {
    xs: {
      height: '24px',
      padding: '0 8px',
      fontSize: SAP_TYPOGRAPHY.fontSize.xs,
      iconSize: '12px',
    },
    sm: {
      height: '32px',
      padding: '0 12px',
      fontSize: SAP_TYPOGRAPHY.fontSize.sm,
      iconSize: '16px',
    },
    md: {
      height: '40px',
      padding: '0 16px',
      fontSize: SAP_TYPOGRAPHY.fontSize.md,
      iconSize: '20px',
    },
    lg: {
      height: '48px',
      padding: '0 24px',
      fontSize: SAP_TYPOGRAPHY.fontSize.lg,
      iconSize: '24px',
    },
    xl: {
      height: '56px',
      padding: '0 32px',
      fontSize: SAP_TYPOGRAPHY.fontSize.xl,
      iconSize: '28px',
    },
  };

  // Variant configurations
  const variantConfig = {
    primary: {
      backgroundColor: SAP_COLORS.sapBlue,
      color: SAP_COLORS.sapWhite,
      hoverBackground: SAP_COLORS.sapDarkBlue,
      hoverColor: SAP_COLORS.sapWhite,
      border: 'none',
      boxShadow: SAP_SHADOWS.sm,
    },
    secondary: {
      backgroundColor: SAP_COLORS.sapGray2,
      color: SAP_COLORS.sapGray7,
      hoverBackground: SAP_COLORS.sapGray3,
      hoverColor: SAP_COLORS.sapGray7,
      border: 'none',
      boxShadow: 'none',
    },
    success: {
      backgroundColor: SAP_COLORS.sapGreen,
      color: SAP_COLORS.sapWhite,
      hoverBackground: '#2E7D32',
      hoverColor: SAP_COLORS.sapWhite,
      border: 'none',
      boxShadow: SAP_SHADOWS.sm,
    },
    warning: {
      backgroundColor: SAP_COLORS.sapOrange,
      color: SAP_COLORS.sapWhite,
      hoverBackground: '#D84315',
      hoverColor: SAP_COLORS.sapWhite,
      border: 'none',
      boxShadow: SAP_SHADOWS.sm,
    },
    error: {
      backgroundColor: SAP_COLORS.sapRed,
      color: SAP_COLORS.sapWhite,
      hoverBackground: '#C62828',
      hoverColor: SAP_COLORS.sapWhite,
      border: 'none',
      boxShadow: SAP_SHADOWS.sm,
    },
    ghost: {
      backgroundColor: 'transparent',
      color: SAP_COLORS.sapGray6,
      hoverBackground: SAP_COLORS.hover,
      hoverColor: SAP_COLORS.sapGray7,
      border: 'none',
      boxShadow: 'none',
    },
    outlined: {
      backgroundColor: 'transparent',
      color: SAP_COLORS.sapGray6,
      hoverBackground: SAP_COLORS.hover,
      hoverColor: SAP_COLORS.sapBlue,
      border: `1px solid ${SAP_COLORS.sapGray3}`,
      boxShadow: 'none',
    },
  };

  const currentSize = sizeConfig[(size as keyof typeof sizeConfig) || 'md'];
  const currentVariant = variantConfig[variant as keyof typeof variantConfig];

  return {
    height: currentSize.height,
    minHeight: currentSize.height,
    padding: currentSize.padding,
    backgroundColor: currentVariant.backgroundColor,
    color: currentVariant.color,
    border: currentVariant.border,
    borderRadius: SAP_SPACING.component.borderRadius,
    fontSize: currentSize.fontSize,
    fontWeight: SAP_TYPOGRAPHY.fontWeight.medium,
    fontFamily: SAP_TYPOGRAPHY.fontFamily.primary,
    textTransform: 'none',
    transition: SAP_TRANSITIONS.fast,
    boxShadow: currentVariant.boxShadow,

    '& .MuiSvgIcon-root': {
      fontSize: currentSize.iconSize,
      transition: SAP_TRANSITIONS.fast,
    },

    '&:hover': {
      backgroundColor: loading ? currentVariant.backgroundColor : currentVariant.hoverBackground,
      color: loading ? currentVariant.color : currentVariant.hoverColor,
      borderColor: variant === 'outlined' ? SAP_COLORS.sapBlue : currentVariant.border,
      boxShadow: loading ? currentVariant.boxShadow : (
        variant === 'primary' || variant === 'success' || variant === 'warning' || variant === 'error'
          ? SAP_SHADOWS.md 
          : SAP_SHADOWS.sm
      ),
      transform: loading ? 'none' : 'translateY(-1px)',
    },

    '&:active': {
      transform: loading ? 'none' : 'translateY(0)',
      boxShadow: loading ? currentVariant.boxShadow : SAP_SHADOWS.sm,
    },

    '&:focus-visible': {
      outline: `2px solid ${SAP_COLORS.sapBlue}`,
      outlineOffset: '2px',
    },

    '&.Mui-disabled': {
      backgroundColor: SAP_COLORS.disabled,
      color: SAP_COLORS.sapGray4,
      borderColor: SAP_COLORS.sapGray3,
      boxShadow: 'none',
      cursor: 'not-allowed',
      transform: 'none',
    },

    ...customStyles,
  };
});

// Styled IconButton for icon-only buttons
const StyledIconButton = styled(IconButton, {
  shouldForwardProp: (prop) => !['size', 'variant', 'loading', 'customStyles'].includes(prop as string),
})<{
  variant?: ButtonVariant;
  size?: ButtonSize;
  loading?: boolean;
  customStyles?: React.CSSProperties;
}>(({ theme, variant = 'secondary', size = 'md', loading, customStyles }) => {
  // Size configurations
  const sizeConfig = {
    xs: {
      width: '24px',
      height: '24px',
      iconSize: '12px',
    },
    sm: {
      width: '32px',
      height: '32px',
      iconSize: '16px',
    },
    md: {
      width: '40px',
      height: '40px',
      iconSize: '20px',
    },
    lg: {
      width: '48px',
      height: '48px',
      iconSize: '24px',
    },
    xl: {
      width: '56px',
      height: '56px',
      iconSize: '28px',
    },
  };

  // Variant configurations (same as button)
  const variantConfig = {
    primary: {
      backgroundColor: SAP_COLORS.sapBlue,
      color: SAP_COLORS.sapWhite,
      hoverBackground: SAP_COLORS.sapDarkBlue,
      hoverColor: SAP_COLORS.sapWhite,
      border: 'none',
      boxShadow: SAP_SHADOWS.sm,
    },
    secondary: {
      backgroundColor: 'transparent',
      color: SAP_COLORS.sapGray6,
      hoverBackground: SAP_COLORS.hover,
      hoverColor: SAP_COLORS.sapBlue,
      border: `1px solid ${SAP_COLORS.sapGray3}`,
      boxShadow: 'none',
    },
    success: {
      backgroundColor: SAP_COLORS.sapGreen,
      color: SAP_COLORS.sapWhite,
      hoverBackground: '#2E7D32',
      hoverColor: SAP_COLORS.sapWhite,
      border: 'none',
      boxShadow: SAP_SHADOWS.sm,
    },
    warning: {
      backgroundColor: SAP_COLORS.sapOrange,
      color: SAP_COLORS.sapWhite,
      hoverBackground: '#D84315',
      hoverColor: SAP_COLORS.sapWhite,
      border: 'none',
      boxShadow: SAP_SHADOWS.sm,
    },
    error: {
      backgroundColor: SAP_COLORS.sapRed,
      color: SAP_COLORS.sapWhite,
      hoverBackground: '#C62828',
      hoverColor: SAP_COLORS.sapWhite,
      border: 'none',
      boxShadow: SAP_SHADOWS.sm,
    },
    ghost: {
      backgroundColor: 'transparent',
      color: SAP_COLORS.sapGray5,
      hoverBackground: SAP_COLORS.hover,
      hoverColor: SAP_COLORS.sapGray7,
      border: 'none',
      boxShadow: 'none',
    },
    outlined: {
      backgroundColor: 'transparent',
      color: SAP_COLORS.sapGray6,
      hoverBackground: SAP_COLORS.hover,
      hoverColor: SAP_COLORS.sapBlue,
      border: `1px solid ${SAP_COLORS.sapGray3}`,
      boxShadow: 'none',
    },
  };

  const currentSize = sizeConfig[(size as keyof typeof sizeConfig) || 'md'];
  const currentVariant = variantConfig[variant];

  return {
    width: currentSize.width,
    height: currentSize.height,
    minWidth: currentSize.width,
    minHeight: currentSize.height,
    backgroundColor: currentVariant.backgroundColor,
    color: currentVariant.color,
    border: currentVariant.border,
    borderRadius: SAP_SPACING.component.borderRadius,
    transition: SAP_TRANSITIONS.fast,
    boxShadow: currentVariant.boxShadow,

    '& .MuiSvgIcon-root': {
      fontSize: currentSize.iconSize,
      transition: SAP_TRANSITIONS.fast,
    },

    '&:hover': {
      backgroundColor: loading ? currentVariant.backgroundColor : currentVariant.hoverBackground,
      color: loading ? currentVariant.color : currentVariant.hoverColor,
      borderColor: variant === 'outlined' ? SAP_COLORS.sapBlue : currentVariant.border,
      boxShadow: loading ? currentVariant.boxShadow : (
        variant === 'primary' || variant === 'success' || variant === 'warning' || variant === 'error'
          ? SAP_SHADOWS.md 
          : SAP_SHADOWS.sm
      ),
      transform: loading ? 'none' : 'translateY(-1px)',
    },

    '&:active': {
      transform: loading ? 'none' : 'translateY(0)',
      boxShadow: loading ? currentVariant.boxShadow : SAP_SHADOWS.sm,
    },

    '&:focus-visible': {
      outline: `2px solid ${SAP_COLORS.sapBlue}`,
      outlineOffset: '2px',
    },

    '&.Mui-disabled': {
      backgroundColor: SAP_COLORS.disabled,
      color: SAP_COLORS.sapGray4,
      borderColor: SAP_COLORS.sapGray3,
      boxShadow: 'none',
      cursor: 'not-allowed',
      transform: 'none',
    },

    ...customStyles,
  };
});

const LoadingOverlay = styled(Box)({
  position: 'absolute',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  backgroundColor: 'rgba(255, 255, 255, 0.8)',
  borderRadius: SAP_SPACING.component.borderRadius,
});

// Main component
export const SAPButton = forwardRef<HTMLButtonElement, SAPButtonProps>(
  (
    {
      variant = 'primary',
      size = 'md',
      fullWidth = false,
      loading = false,
      loadingText = 'Loading...',
      icon,
      iconPosition = 'start',
      iconOnly = false,
      badge,
      badgeColor = 'error',
      tooltip,
      customStyles,
      onClick,
      children,
      disabled,
      ariaLabel,
      ariaDescribedBy,
      ...props
    },
    ref
  ) => {
    // Extract customStyles to prevent it from being passed to DOM
    const { customStyles: _ignoredCustomStyles, ...domProps } = props as any;
    
    // Get icon component
    const IconComponent = useMemo(() => {
      if (typeof icon === 'string' && icon in iconMap) {
        return iconMap[icon as IconType];
      }
      return icon;
    }, [icon]);

    // Enhanced accessibility props
    const accessibilityProps = {
      'aria-label': ariaLabel,
      'aria-describedby': ariaDescribedBy,
      'aria-busy': loading,
    };

    // Render icon
    const renderIcon = () => {
      if (!IconComponent) return null;
      
      if (React.isValidElement(IconComponent)) {
        return IconComponent;
      }
      
      if (typeof IconComponent === 'function') {
        return <IconComponent />;
      }
      
      return null;
    };

    // Render button content
    const renderButtonContent = () => {
      if (loading) {
        return (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <CircularProgress
              size={size === 'xs' ? 12 : size === 'sm' ? 16 : size === 'md' ? 20 : size === 'lg' ? 24 : 28}
              thickness={4}
              sx={{
                color: variant === 'primary' || variant === 'success' || variant === 'warning' || variant === 'error'
                  ? SAP_COLORS.sapWhite
                  : SAP_COLORS.sapBlue,
              }}
            />
            {!iconOnly && <span>{loadingText}</span>}
          </Box>
        );
      }

      if (iconOnly) {
        return renderIcon();
      }

      const iconElement = renderIcon();
      
      if (iconElement && iconPosition === 'start') {
        return (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {iconElement}
            {children}
          </Box>
        );
      }
      
      if (iconElement && iconPosition === 'end') {
        return (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {children}
            {iconElement}
          </Box>
        );
      }
      
      return children;
    };

    // Create button element
    const buttonElement = iconOnly ? (
      <StyledIconButton
        ref={ref}
        variant={variant}
        size={size}
        loading={loading}
        onClick={onClick}
        disabled={disabled || loading}
        {...accessibilityProps}
        {...domProps}
        sx={{
          position: 'relative',
          ...customStyles,
          ...domProps.sx,
        }}
      >
        {renderButtonContent()}
        {loading && (
          <LoadingOverlay>
            <CircularProgress
              size={size === 'xs' ? 12 : size === 'sm' ? 16 : size === 'md' ? 20 : size === 'lg' ? 24 : 28}
              thickness={4}
              sx={{
                color: variant === 'primary' || variant === 'success' || variant === 'warning' || variant === 'error'
                  ? SAP_COLORS.sapWhite
                  : SAP_COLORS.sapBlue,
              }}
            />
          </LoadingOverlay>
        )}
      </StyledIconButton>
    ) : (
      <StyledButton
        ref={ref}
        variant={variant}
        size={size}
        fullWidth={fullWidth}
        loading={loading}
        onClick={onClick}
        disabled={disabled || loading}
        {...accessibilityProps}
        {...domProps}
        sx={{
          position: 'relative',
          ...customStyles,
          ...domProps.sx,
        }}
      >
        {renderButtonContent()}
        {loading && (
          <LoadingOverlay>
            <CircularProgress
              size={size === 'xs' ? 12 : size === 'sm' ? 16 : size === 'md' ? 20 : size === 'lg' ? 24 : 28}
              thickness={4}
              sx={{
                color: variant === 'primary' || variant === 'success' || variant === 'warning' || variant === 'error'
                  ? SAP_COLORS.sapWhite
                  : SAP_COLORS.sapBlue,
              }}
            />
          </LoadingOverlay>
        )}
      </StyledButton>
    );

    // Add badge if provided
    const buttonWithBadge = badge ? (
      <Badge
        badgeContent={badge}
        color={badgeColor}
        sx={{
          '& .MuiBadge-badge': {
            backgroundColor: badgeColor === 'error' ? SAP_COLORS.sapRed : 
                            badgeColor === 'warning' ? SAP_COLORS.sapOrange :
                            badgeColor === 'success' ? SAP_COLORS.sapGreen :
                            badgeColor === 'info' ? SAP_COLORS.sapBlue :
                            badgeColor === 'primary' ? SAP_COLORS.sapBlue :
                            SAP_COLORS.sapGray5,
            color: SAP_COLORS.sapWhite,
            fontSize: '0.625rem',
            fontWeight: 600,
            minWidth: '18px',
            height: '18px',
          },
        }}
      >
        {buttonElement}
      </Badge>
    ) : buttonElement;

    // Add tooltip if provided
    if (tooltip) {
      return (
        <Tooltip
          title={tooltip}
          arrow
          placement="top"
          sx={{
            '& .MuiTooltip-tooltip': {
              backgroundColor: SAP_COLORS.sapGray7,
              color: SAP_COLORS.sapWhite,
              fontSize: '0.75rem',
              fontWeight: 500,
              padding: `${SAP_SPACING.xs} ${SAP_SPACING.sm}`,
              borderRadius: SAP_SPACING.component.borderRadius,
            },
            '& .MuiTooltip-arrow': {
              color: SAP_COLORS.sapGray7,
            },
          }}
        >
          <span>
            {buttonWithBadge}
          </span>
        </Tooltip>
      );
    }

    return buttonWithBadge;
  }
);

// Display name for debugging
SAPButton.displayName = 'SAPButton';

// Export types for external use
export { buttonVariants };
